import React, { createContext, useContext, useState, ReactNode } from 'react'

interface EditModeContextType {
  isEditMode: boolean
  setIsEditMode: (value: boolean) => void
  suppressAutofill: boolean
}

const EditModeContext = createContext<EditModeContextType | undefined>(undefined)

export const EditModeProvider = ({
  children,
  initialEditMode = false,
  forceEditMode = false,
  suppressAutofill: suppressAutofillProp,
}: {
  children: ReactNode
  initialEditMode?: boolean
  /** Keeps form fields editable regardless of edit-mode toggles. */
  forceEditMode?: boolean
  /** Blocks Chrome/browser saved contact & address suggestions on in-app forms. */
  suppressAutofill?: boolean
}) => {
  const parentContext = useContext(EditModeContext)
  const [isEditMode, setIsEditMode] = useState(initialEditMode)
  const suppressAutofill =
    suppressAutofillProp ?? parentContext?.suppressAutofill ?? false

  return (
    <EditModeContext.Provider
      value={{
        isEditMode: forceEditMode || isEditMode,
        setIsEditMode,
        suppressAutofill,
      }}
    >
      {children}
    </EditModeContext.Provider>
  )
}

export const useEditMode = () => {
  const context = useContext(EditModeContext)
  if (!context) {
    return {
      isEditMode: false,
      hasContext: false,
      suppressAutofill: false,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      setIsEditMode: () => {},
    }
  }
  return { ...context, hasContext: true }
}
